class CustomMenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    // Bind hover events for desktop
    this.addEventListener('mouseenter', this.handleHover.bind(this));
    this.addEventListener('mouseleave', this.handleLeave.bind(this));

    // Click outside the menu box closes it (bound while the menu is open)
    this.onDocumentClick = this.handleDocumentClick.bind(this);
  }

  handleHover() {
    if (!window.matchMedia('(hover: hover)').matches) return;
    if (this.closeTimeout) clearTimeout(this.closeTimeout);
    if (!this.classList.contains('active')) {
      this.open(this.triggeredBy);
    }
  }

  handleLeave() {
    if (!window.matchMedia('(hover: hover)').matches) return;
    this.closeTimeout = setTimeout(() => {
      this.close();
    }, 200);
  }

  handleDocumentClick(event) {
    if (!this.classList.contains('active')) {
      document.removeEventListener('click', this.onDocumentClick);
      return;
    }

    const target = event.target;
    if (!(target instanceof Node)) return;

    // Click happened inside the drawer element
    if (this.contains(target)) {
      // Clicking the dark backdrop closes it
      if (target.closest && target.closest('.custom-menu-drawer__overlay')) {
        this.close();
        return;
      }
      // If the drawer element itself is a full-screen layer, clicking its empty area is "outside"
      if (target === this) {
        const rect = this.getBoundingClientRect();
        const isFullScreen = rect.width >= window.innerWidth * 0.95 && rect.height >= window.innerHeight * 0.95;
        if (isFullScreen) this.close();
      }
      return;
    }

    // Clicks on the header bar (the menu links / icons) are handled by their own code
    if (target.closest && target.closest('.header-wrapper')) return;

    // Anywhere else on the page = outside the menu box
    this.close();
  }

  connectedCallback() {
    // Teleport to body to fix stacking context issues (e.g. overlap with announcement bar)
    if (this.parentNode !== document.body) {
      const existing = document.body.querySelector(`#${this.id}`);
      if (existing && existing !== this) {
        existing.remove();
      }
      document.body.appendChild(this);
      return;
    }
    const overlay = this.querySelector('.custom-menu-drawer__overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    // Bind click events for navigation
    this.querySelectorAll('[data-target-view]').forEach(btn => {
      if (btn.dataset.bound) return;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const targetId = btn.getAttribute('data-target-view');
        this.switchView(targetId);
      });
      btn.dataset.bound = "true";
    });
  }
  toggle(triggeredBy) {
    if (this.classList.contains('active')) {
      this.close();
    } else {
      this.open(triggeredBy);
    }
  }
  open(triggeredBy) {
    if (triggeredBy) this.triggeredBy = triggeredBy;
    this.classList.add('active');

    // Reset to root view on open
    this.switchView('root', false);
    const closeBtn = this.querySelector('.custom-menu-drawer__close');
    if (closeBtn) closeBtn.focus();

    if (!window.matchMedia('(min-width: 750px)').matches) {
      if (window.toggleScroll) {
        window.toggleScroll(false);
      } else {
        document.body.classList.add('overflow-hidden');
      }
      document.body.classList.add('overlay-open');
    }

    const header = document.querySelector('.header-wrapper');
    if (header) header.classList.add('menu-open');

    // Start listening for outside clicks after the click that opened the menu has finished
    setTimeout(() => {
      if (this.classList.contains('active')) {
        document.addEventListener('click', this.onDocumentClick);
      }
    }, 0);
  }
  close() {
    this.classList.remove('active');
    document.removeEventListener('click', this.onDocumentClick);

    if (!window.matchMedia('(min-width: 750px)').matches) {
      if (window.toggleScroll) {
        window.toggleScroll(true);
      } else {
        document.body.classList.remove('overflow-hidden');
      }
      document.body.classList.remove('overlay-open');
    }

    const header = document.querySelector('.header-wrapper');
    if (header) header.classList.remove('menu-open');

    if (this.triggeredBy) {
      this.triggeredBy.focus();
      this.triggeredBy = null;
    }
  }
  switchView(viewId, animate = true) {
    const views = this.querySelectorAll('.custom-menu-drawer__view');
    const targetView = this.querySelector(`.custom-menu-drawer__view[data-view-id="${viewId}"]`);

    if (!targetView) return;
    // Hide all views
    views.forEach(view => {
      view.classList.remove('custom-menu-drawer__view--active');
      if (!animate) {
        view.style.transition = 'none';
      } else {
        view.style.transition = ''; // Restore CSS transition
      }
    });
    // Show target view
    if (!animate) {
      targetView.style.transition = 'none';
      targetView.classList.add('custom-menu-drawer__view--active');
      // Force reflow
      void targetView.offsetWidth;
      targetView.style.transition = '';
    } else {
      targetView.classList.add('custom-menu-drawer__view--active');
    }

    // Scroll top
    targetView.scrollTop = 0;
  }
}
customElements.define('custom-menu-drawer', CustomMenuDrawer);
